import { useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { IconColumns2, IconEdit, IconEye } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';

/** The brief stays descriptive; only the mission's reviewed explicit actions execute. */
export const MissionBriefEditor = ({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) => {
  const [mode, setMode] = useState<'write' | 'split' | 'preview'>('split');
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Mission brief
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'surface.raised',
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}>
          <Typography variant="caption" color="text.secondary">
            Markdown
          </Typography>
          <Stack direction="row" gap={0.5}>
            {(
              [
                { id: 'write', label: 'Write', icon: IconEdit },
                { id: 'split', label: 'Split', icon: IconColumns2 },
                { id: 'preview', label: 'Preview', icon: IconEye },
              ] as const
            ).map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                size="small"
                startIcon={<Icon size={14} />}
                aria-pressed={mode === id}
                onClick={() => setMode(id)}
                sx={{
                  color: mode === id ? 'primary.main' : 'text.secondary',
                  bgcolor: mode === id ? 'primary.light' : 'transparent',
                }}>
                {label}
              </Button>
            ))}
          </Stack>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: mode === 'split' ? '1fr 1fr' : '1fr' },
            minHeight: 300,
          }}>
          <Box
            sx={{
              display: mode === 'preview' ? 'none' : 'block',
              minWidth: 0,
              borderInlineEnd: { md: mode === 'split' ? 1 : 0 },
              borderColor: 'divider',
            }}>
            <TextField
              label="Mission brief"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              multiline
              minRows={10}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 4000, 'aria-label': 'Mission brief' } }}
              sx={{
                '& .MuiInputLabel-root': { display: 'none' },
                '& .MuiOutlinedInput-notchedOutline': { border: 0 },
                '& .MuiInputBase-root': {
                  p: 3,
                  borderRadius: 0,
                  fontFamily: 'monospace',
                  alignItems: 'start',
                  bgcolor: 'transparent',
                },
              }}
            />
          </Box>
          {mode !== 'write' && (
            <Box
              component="section"
              aria-label="Mission brief preview"
              sx={{
                p: 3,
                minWidth: 0,
                overflowWrap: 'anywhere',
                '& p:first-of-type': { mt: 0 },
                '& pre': { whiteSpace: 'pre-wrap' },
                '& img': { maxWidth: '100%' },
              }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Live preview
              </Typography>
              {value ? (
                <ReactMarkdown>{value}</ReactMarkdown>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Describe the outcome and context for this mission.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Describe the intent. Only the explicit actions in Access execute.
      </Typography>
    </Box>
  );
};
